import { useState } from "react";
import { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { IMaterialsResponse } from "@miplatform/types";
import {
  getSelectedAttributeValues,
  simpleSort,
  sortCategoryAttributes,
} from "@miplatform/utils";
import { IAddRequirementsData } from "../../schema/addRequirements.schema";

type RequirementItem = IMaterialsResponse & {
  isNew?: boolean;
  removed?: boolean;
};

export const useRequirementItemsViewModel = ({
  setValue,
  getValues,
}: {
  setValue: UseFormSetValue<IAddRequirementsData>;
  getValues: UseFormGetValues<IAddRequirementsData>;
}) => {
  const stateManager = useComponentState();

  const [materials, setMaterials] = useState<IMaterialsResponse[]>([]);
  const [filteredMaterials, setFilteredMaterials] = useState<
    IMaterialsResponse[]
  >([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<any>({});
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<
    Record<string, string[]>
  >({});
  const [categoryFilterOptions, setCategoryFilterOptions] = useState<any[]>([]);
  const [openMaterialResults, setOpenMaterialResults] = useState(false);
  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false);
  const [requirementItems, setRequirementItems] = useState<RequirementItem[]>(
    []
  );

  const debouncedSearch = useDebounce(search, 300);
  const hasFilters = !!selectedCategory?.categoryId || !!debouncedSearch;

  const getCategoryFilters = () => {
    stateManager.run(async () => {
      const response = await homeService.categories.getFilters();
      if (response.status === HTTP_STATUSES.OK) {
        setCategoryFilterOptions(response.data);
      }
    });
  };

  const getMaterials = () => {
    stateManager.run(async () => {
      const response = await homeService.materials.getAll({
        sort: { category: ["categoryName", "itemCode"] },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setMaterials(response.data);
      }
    });
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setOpenMaterialResults(true);
  };

  const handleCategoryClick = (category: typeof selectedCategory) => {
    setSelectedCategory(sortCategoryAttributes(category));
    setOpenMaterialResults(true);
  };

  const handleAttributeValueClick = (
    attributeId: string,
    attributeValue: string
  ) => {
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
  };

  const handleResetSelection = () => {
    setSelectedAttributeValues({});
    setSearch("");
  };

  const handleReset = () => {
    setSelectedCategory({});
    handleResetSelection();
  };

  const handleResetResultContainer = () => {
    setSearch("");
    setSelectedAttributeValues({});
    setSelectedCategory({});
    setFilteredMaterials([]);
    setOpenMaterialResults(false);
  };

  const handleCloseCategoryDrawer = () => {
    setOpenCategoryDrawer(false);
    handleResetResultContainer();
  };

  const isDataSelected = (item: IMaterialsResponse) =>
    requirementItems.some((d) => d.id === item.id && !d.removed);

  const removeData = (
    data: RequirementItem[],
    id: string
  ): RequirementItem[] => {
    const isNewItem = data.some((d) => d.id === id && d.isNew);
    const softRemovedData = data.map((d) =>
      d.id === id ? { ...d, removed: true } : d
    );
    const removedData = data.filter((d) => d.id !== id);
    return isNewItem ? removedData : softRemovedData;
  };

  const addData = (
    data: RequirementItem[],
    item: IMaterialsResponse
  ): RequirementItem[] => {
    const itemExists = data.some((d) => d.id === item.id);
    const undoRemovedData = data.map((d) =>
      d.id === item.id ? { ...item, removed: false } : d
    );
    const newData = [...data, { ...item, isNew: true }];
    return itemExists ? undoRemovedData : newData;
  };

  const handleAddRemoveData = (item: IMaterialsResponse) => {
    const isSelected = isDataSelected(item);
    const updatedData = isSelected
      ? removeData(requirementItems, item.id)
      : addData(requirementItems, item);
    const sorted = updatedData.toSorted((a, b) =>
      simpleSort(a.category?.itemCode, b.category?.itemCode)
    );
    setRequirementItems(sorted);
    const ids = sorted.filter((d) => !d.removed).map((d) => d.id);
    setValue("materialIds", ids, { shouldValidate: true, shouldDirty: true });
    if (isSelected) {
      const { [item.id]: _, ...remainingAreas } =
        getValues("materialAreas") ?? {};
      setValue("materialAreas", remainingAreas, {
        shouldValidate: true,
        shouldDirty: true,
      });
      const { [item.id]: _removedQuantity, ...remainingQuantities } =
        getValues("materialQuantities") ?? {};
      setValue("materialQuantities", remainingQuantities, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue(
        "materialQuantities",
        { ...getValues("materialQuantities"), [item.id]: 1 },
        { shouldValidate: true, shouldDirty: true }
      );
    }
  };

  const handleUpdateItemQuantity = (materialId: string, quantity: number) => {
    setValue(
      "materialQuantities",
      { ...getValues("materialQuantities"), [materialId]: quantity },
      { shouldValidate: true, shouldDirty: true }
    );
  };

  return {
    isLoading: stateManager.isBusy(),
    materials,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    debouncedSearch,
    search,
    filteredMaterials,
    openMaterialResults,
    openCategoryDrawer,
    hasFilters,
    requirementItems,
    getMaterials,
    getCategoryFilters,
    handleSearch,
    handleCategoryClick,
    handleAttributeValueClick,
    handleReset,
    handleResetResultContainer,
    handleCloseCategoryDrawer,
    handleAddRemoveData,
    handleUpdateItemQuantity,
    isDataSelected,
    setFilteredMaterials,
    setOpenCategoryDrawer,
    setRequirementItems,
  };
};
